// src/common/components/cartModal/components/checkout-stage/components/orderSummary/OrderSummary.tsx
import React from "react";
import { useSelector } from "react-redux";
import styles from "./OrderSummary.module.scss";
import {
  CartItem,
  CdekCalculateResponse,
  DeliveryRule,
} from "../../../../types/types";
import { DeliveryService, DeliveryType } from "../../../../types/enums";
import { useTheme } from "../../../../hooks/useTheme";
import { selectSaleItemById } from "../../../../../store/slices/saleItemListSlice";
import { RootState } from "../../../../../store/store";

interface OrderSummaryProps {
  orderItems: CartItem[];
  remainingItems: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  getSaleItemName: (id: number) => string;
  discountAmount?: number;
  bonusesSpent?: number;
  bonusesToAdd?: number;
  hasPromo?: boolean;
  deliveryType?: DeliveryType;
  bestDeliveryText: string | null;
  storeName?: string;
  currentBestRule?: DeliveryRule | null;
  nextBestRule?: DeliveryRule | null;
  bestPossibleRule?: DeliveryRule | null;
  deliveryAvailable?: boolean;
  deliveryError?: string | null;
  selectedAddressId?: number | null;
  selectedGiftItemId?: number;
  deliveryService?: DeliveryService;
  cdekOptions?: CdekCalculateResponse | null;
  cutleryCount?: number;
}

export default function OrderSummary({
  orderItems,
  remainingItems,
  subtotal,
  deliveryFee,
  total,
  getSaleItemName,
  discountAmount = 0,
  bonusesSpent = 0,
  bonusesToAdd = 0,
  hasPromo = false,
  deliveryType,
  bestDeliveryText,
  storeName,
  currentBestRule,
  nextBestRule,
  bestPossibleRule,
  deliveryAvailable,
  deliveryError,
  selectedAddressId,
  selectedGiftItemId,
  deliveryService,
  cdekOptions,
  cutleryCount,
}: OrderSummaryProps) {
  const { theme, isDark } = useTheme();

  // Получаем товар-подарок по ID
  const selectedGiftItem = useSelector((state: RootState) =>
    selectedGiftItemId ? selectSaleItemById(state, selectedGiftItemId) : null,
  );

  const bonusColor = "#FF9800";
  const spentBonusColor = "#F44336";
  const warningColor = "#FF9800";
  const errorColor = "#F44336";

  const totalWithoutDiscounts = subtotal + deliveryFee;
  const savings = totalWithoutDiscounts - total - bonusesSpent;
  const isCdekDelivery = deliveryService === DeliveryService.CDEK;
  const showBestConditions =
    !isCdekDelivery && (currentBestRule || nextBestRule || bestPossibleRule);
  const hasCdekOptions = !!cdekOptions?.tariffs?.length;
  const showCutlery = cutleryCount !== undefined && cutleryCount > 0;

  if (deliveryType === DeliveryType.DELIVERY) {
    // Адрес не выбран
    if (!selectedAddressId) {
      return (
        <div className={styles.container}>
          <h3 className={styles.title} style={{ color: theme.text }}>
            Состав заказа
          </h3>
          <div
            className={styles.remainingSection}
            style={{ borderColor: `${warningColor}40` }}
          >
            <h4
              className={styles.remainingTitle}
              style={{ color: warningColor }}
            >
              ⚠️ Выберите адрес доставки
            </h4>
            <div className={styles.errorText} style={{ color: warningColor }}>
              Пожалуйста, выберите или добавьте адрес для расчета доставки
            </div>
          </div>
        </div>
      );
    }

    // Адрес выбран, но своя доставка недоступна
    if (!isCdekDelivery && !hasCdekOptions && deliveryAvailable === false) {
      return (
        <div className={styles.container}>
          <h3 className={styles.title} style={{ color: theme.text }}>
            Состав заказа
          </h3>
          <div
            className={styles.remainingSection}
            style={{ borderColor: `${errorColor}40` }}
          >
            <h4 className={styles.remainingTitle} style={{ color: errorColor }}>
              ❌ Доставка недоступна
            </h4>
            <div className={styles.errorText} style={{ color: errorColor }}>
              {deliveryError || "К сожалению, мы не доставляем по этому адресу"}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <h3 className={styles.title} style={{ color: theme.text }}>
          Состав заказа
        </h3>

        <div className={styles.orderItems}>
          {orderItems.map((item, index) => (
            <div
              key={index}
              className={styles.orderItem}
              style={{ borderBottomColor: `${theme.border}20` }}
            >
              <span className={styles.itemName} style={{ color: theme.text }}>
                {getSaleItemName(Number(item.id))}
              </span>
              <span
                className={styles.itemQuantity}
                style={{ color: theme.textSecondary }}
              >
                {item.quantity} ×
              </span>
              <span className={styles.itemPrice} style={{ color: theme.text }}>
                {Number(item.price).toLocaleString("ru-RU")} ₽
              </span>
            </div>
          ))}

          {/* Подарок */}
          {selectedGiftItem && (
            <div
              className={styles.orderItem}
              style={{ borderBottomColor: `${theme.border}20` }}
            >
              <span
                className={styles.itemName}
                style={{ color: theme.success }}
              >
                🎁 {selectedGiftItem.name} (подарок)
              </span>
              <span
                className={styles.itemQuantity}
                style={{ color: theme.textSecondary }}
              >
                1 ×
              </span>
              <span
                className={styles.itemPrice}
                style={{ color: theme.success }}
              >
                0 ₽
              </span>
            </div>
          )}

          {/* Приборы */}
          {/* Приборы */}
          {showCutlery && (
            <div
              className={styles.orderItem}
              style={{ borderBottomColor: `${theme.border}20` }}
            >
              <span className={styles.itemName}>🍴 Приборы</span>
              <span className={styles.itemQuantity}>{cutleryCount} ×</span>
              <span className={styles.itemPrice}>0 ₽</span>
            </div>
          )}
        </div>

        {remainingItems.length > 0 && (
          <div
            className={styles.remainingSection}
            style={{ borderColor: `${warningColor}40` }}
          >
            <h4
              className={styles.remainingTitle}
              style={{ color: warningColor }}
            >
              ⚠️ Недоступны в {storeName || "выбранном магазине"}
            </h4>
            {remainingItems.map((item, index) => {
              const saleItem = getSaleItemName(Number(item.id));
              return (
                <div
                  key={index}
                  className={styles.remainingItem}
                  style={{ borderBottomColor: theme.border }}
                >
                  <span
                    className={styles.itemName}
                    style={{ color: theme.text }}
                  >
                    {saleItem}
                  </span>
                  <span
                    className={styles.itemQuantity}
                    style={{ color: theme.textSecondary }}
                  >
                    {item.quantity} шт
                  </span>
                </div>
              );
            })}
            <p className={styles.deliveryNote} style={{ color: warningColor }}>
              Эти товары останутся в корзине. Вы можете выбрать другой магазин
              или заказать их с доставкой.
            </p>
          </div>
        )}

        <div
          className={styles.summaryRow}
          style={{ borderTopColor: theme.border }}
        >
          <span className={styles.summaryLabel} style={{ color: theme.text }}>
            Подытог:
          </span>
          <span className={styles.summaryValue} style={{ color: theme.text }}>
            {Number(subtotal).toLocaleString("ru-RU")} ₽
          </span>
        </div>

        {discountAmount > 0 && (
          <div className={styles.discountRow}>
            <div className={styles.discountLabelWrapper}>
              <span
                className={styles.discountLabel}
                style={{ color: theme.primary }}
              >
                Скидка
              </span>
              {hasPromo && (
                <span
                  className={styles.promoBadge}
                  style={{ backgroundColor: `${theme.primary}20` }}
                >
                  <span
                    className={styles.promoBadgeText}
                    style={{ color: theme.primary }}
                  >
                    Промо
                  </span>
                </span>
              )}
            </div>
            <span
              className={styles.discountValue}
              style={{ color: theme.primary }}
            >
              -{Number(discountAmount).toLocaleString("ru-RU")} ₽
            </span>
          </div>
        )}

        {bonusesSpent > 0 && (
          <div className={styles.spentBonusesRow}>
            <span
              className={styles.spentBonusesLabel}
              style={{ color: spentBonusColor }}
            >
              Списано бонусами
            </span>
            <span
              className={styles.spentBonusesValue}
              style={{ color: spentBonusColor }}
            >
              -{Number(bonusesSpent).toLocaleString("ru-RU")}
            </span>
          </div>
        )}

        <div className={styles.summaryRow}>
          <div>
            <span className={styles.summaryLabel} style={{ color: theme.text }}>
              {isCdekDelivery ? "Доставка СДЭК:" : "Доставка:"}
            </span>
            {deliveryFee === 0 && !isCdekDelivery && (
              <span
                className={styles.deliveryNote}
                style={{ color: warningColor }}
              >
                Бесплатно
              </span>
            )}
          </div>
          <span className={styles.summaryValue} style={{ color: theme.text }}>
            {Number(deliveryFee).toLocaleString("ru-RU")} ₽
          </span>
        </div>

        {showBestConditions && (
          <div
            className={styles.deliveryInfo}
            style={{
              backgroundColor: theme.card,
              borderColor: theme.border,
            }}
          >
            {currentBestRule && (
              <div className={styles.deliveryInfoRow}>
                <span
                  className={styles.deliveryInfoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  Текущие условия:
                </span>
                <span
                  className={styles.deliveryInfoValue}
                  style={{ color: theme.primary }}
                >
                  {currentBestRule.value === 0
                    ? "Бесплатно"
                    : `${currentBestRule.value} ₽`}
                </span>
              </div>
            )}

            {nextBestRule && (
              <div className={styles.deliveryInfoRow}>
                <span
                  className={styles.deliveryInfoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  До следующей цели:
                </span>
                <span
                  className={styles.deliveryInfoValue}
                  style={{ color: theme.primary }}
                >
                  +{nextBestRule.level - subtotal} ₽
                </span>
              </div>
            )}

            {nextBestRule && (
              <div className={styles.deliveryInfoRow}>
                <span
                  className={styles.deliveryInfoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  Стоимость доставки станет:
                </span>
                <span
                  className={styles.deliveryInfoValue}
                  style={{ color: theme.primary }}
                >
                  {nextBestRule.value === 0
                    ? "Бесплатно"
                    : `${nextBestRule.value} ₽`}
                </span>
              </div>
            )}

            {bestPossibleRule &&
              bestPossibleRule.value !== currentBestRule?.value && (
                <div className={styles.deliveryInfoRow}>
                  <span
                    className={styles.deliveryInfoLabel}
                    style={{ color: theme.textSecondary }}
                  >
                    Лучшие условия:
                  </span>
                  <span
                    className={styles.deliveryInfoValue}
                    style={{ color: theme.primary }}
                  >
                    {bestPossibleRule.value === 0
                      ? "Бесплатно"
                      : `${bestPossibleRule.value} ₽`}
                  </span>
                </div>
              )}
          </div>
        )}

        {!isCdekDelivery && bestDeliveryText && (
          <p className={styles.deliveryNote} style={{ color: warningColor }}>
            {bestDeliveryText}
          </p>
        )}

        {bonusesToAdd > 0 && (
          <div className={styles.bonusesRow}>
            <span className={styles.bonusesLabel} style={{ color: bonusColor }}>
              Начислим бонусов:
            </span>
            <span className={styles.bonusesValue} style={{ color: bonusColor }}>
              +{Number(bonusesToAdd).toLocaleString("ru-RU")}
            </span>
          </div>
        )}

        <div
          className={styles.totalRow}
          style={{ borderTopColor: theme.primary }}
        >
          <div>
            <span className={styles.totalLabel} style={{ color: theme.text }}>
              Итого к оплате:
            </span>
            {savings > 0 && (
              <div
                className={styles.savingsBadge}
                style={{ backgroundColor: `${theme.primary}20` }}
              >
                <span
                  className={styles.savingsText}
                  style={{ color: theme.primary }}
                >
                  Экономия: {Number(savings).toLocaleString("ru-RU")} ₽
                </span>
              </div>
            )}
          </div>
          <span className={styles.totalValue} style={{ color: theme.primary }}>
            {Number(total).toLocaleString("ru-RU")} ₽
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title} style={{ color: theme.text }}>
        Состав заказа
      </h3>

      <div className={styles.orderItems}>
        {orderItems.map((item, index) => (
          <div
            key={index}
            className={styles.orderItem}
            style={{ borderBottomColor: `${theme.border}20` }}
          >
            <span className={styles.itemName} style={{ color: theme.text }}>
              {getSaleItemName(Number(item.id))}
            </span>
            <span
              className={styles.itemQuantity}
              style={{ color: theme.textSecondary }}
            >
              {item.quantity} ×
            </span>
            <span className={styles.itemPrice} style={{ color: theme.text }}>
              {Number(item.price).toLocaleString("ru-RU")} ₽
            </span>
          </div>
        ))}

        {/* Подарок */}
        {selectedGiftItem && (
          <div
            className={styles.orderItem}
            style={{ borderBottomColor: `${theme.border}20` }}
          >
            <span className={styles.itemName} style={{ color: theme.success }}>
              🎁 {selectedGiftItem.name} (подарок)
            </span>
            <span
              className={styles.itemQuantity}
              style={{ color: theme.textSecondary }}
            >
              1 ×
            </span>
            <span className={styles.itemPrice} style={{ color: theme.success }}>
              0 ₽
            </span>
          </div>
        )}

        {/* Приборы */}
        {showCutlery && (
          <div
            className={styles.orderItem}
            style={{ borderBottomColor: `${theme.border}20` }}
          >
            <span className={styles.itemName} style={{ color: theme.text }}>
              🍴 Приборы
            </span>
            <span
              className={styles.itemQuantity}
              style={{ color: theme.textSecondary }}
            >
              {cutleryCount} ×
            </span>
            <span
              className={styles.itemPrice}
              style={{ color: theme.textSecondary }}
            >
              0 ₽
            </span>
          </div>
        )}
      </div>

      {remainingItems.length > 0 && (
        <div
          className={styles.remainingSection}
          style={{ borderColor: `${warningColor}40` }}
        >
          <h4 className={styles.remainingTitle} style={{ color: warningColor }}>
            ⚠️ Недоступны в {storeName || "выбранном магазине"}
          </h4>
          {remainingItems.map((item, index) => {
            const saleItem = getSaleItemName(Number(item.id));
            return (
              <div
                key={index}
                className={styles.remainingItem}
                style={{ borderBottomColor: theme.border }}
              >
                <span className={styles.itemName} style={{ color: theme.text }}>
                  {saleItem}
                </span>
                <span
                  className={styles.itemQuantity}
                  style={{ color: theme.textSecondary }}
                >
                  {item.quantity} шт
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div
        className={styles.summaryRow}
        style={{ borderTopColor: theme.border }}
      >
        <span className={styles.summaryLabel} style={{ color: theme.text }}>
          Подытог:
        </span>
        <span className={styles.summaryValue} style={{ color: theme.text }}>
          {Number(subtotal).toLocaleString("ru-RU")} ₽
        </span>
      </div>

      {discountAmount > 0 && (
        <div className={styles.discountRow}>
          <span
            className={styles.discountLabel}
            style={{ color: theme.primary }}
          >
            Скидка
          </span>
          <span
            className={styles.discountValue}
            style={{ color: theme.primary }}
          >
            -{Number(discountAmount).toLocaleString("ru-RU")} ₽
          </span>
        </div>
      )}

      {bonusesSpent > 0 && (
        <div className={styles.spentBonusesRow}>
          <span
            className={styles.spentBonusesLabel}
            style={{ color: spentBonusColor }}
          >
            Списано бонусами
          </span>
          <span
            className={styles.spentBonusesValue}
            style={{ color: spentBonusColor }}
          >
            -{Number(bonusesSpent).toLocaleString("ru-RU")}
          </span>
        </div>
      )}

      {bonusesToAdd > 0 && (
        <div className={styles.bonusesRow}>
          <span className={styles.bonusesLabel} style={{ color: bonusColor }}>
            Начислим бонусов:
          </span>
          <span className={styles.bonusesValue} style={{ color: bonusColor }}>
            +{Number(bonusesToAdd).toLocaleString("ru-RU")}
          </span>
        </div>
      )}

      <div
        className={styles.totalRow}
        style={{ borderTopColor: theme.primary }}
      >
        <span className={styles.totalLabel} style={{ color: theme.text }}>
          Итого:
        </span>
        <span className={styles.totalValue} style={{ color: theme.primary }}>
          {Number(total).toLocaleString("ru-RU")} ₽
        </span>
      </div>
    </div>
  );
}
